import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ApiError, api } from '@/api/client'
import type { Offer, PropertyDetail, RoomType } from '@/api/types'
import * as d from '@/lib/dates'
import { bed, guests, money, nights } from '@/lib/format'

import type { Search } from './SearchBar'
import { SearchBar } from './SearchBar'

interface Props {
  search: Search
  onSearch(next: Search): void
  /** Задан в режиме одного объекта, когда в адресе slug отсутствует. */
  slug?: string
}

export function PropertyPage({ search, onSearch, slug: fixed }: Props) {
  const params = useParams()
  const slug = fixed ?? params.slug ?? ''
  const navigate = useNavigate()
  const [lightbox, setLightbox] = useState<number | null>(null)

  const hasDates = Boolean(search.checkIn && search.checkOut)

  const property = useQuery({
    queryKey: ['property', slug],
    queryFn: () => api.property(slug),
  })

  const offers = useQuery({
    queryKey: ['offers', slug, search],
    queryFn: () =>
      api.offers(slug, {
        check_in: search.checkIn,
        check_out: search.checkOut,
        adults: search.adults,
        children: search.children,
      }),
    enabled: hasDates,
  })

  if (property.isLoading) return <p className="muted section">Загружаем…</p>
  if (property.isError || !property.data) {
    return (
      <p className="error section">
        {property.error instanceof ApiError ? property.error.message : 'Объект не найден'}
      </p>
    )
  }

  const p = property.data
  const nightCount = hasDates ? d.nightsBetween(search.checkIn, search.checkOut) : 0
  const byId = new Map(p.room_types.map((rt) => [rt.id, rt]))

  function book(offer: Offer) {
    const q = new URLSearchParams({
      check_in: search.checkIn,
      check_out: search.checkOut,
      adults: String(search.adults),
      children: String(search.children),
      room_type: offer.room_type_id,
      rate_plan: offer.rate_plan_id,
    })
    navigate(`/hotel/${slug}/book?${q}`)
  }

  return (
    <>
      <Gallery photos={p.photos} name={p.name} onOpen={setLightbox} />

      <section className="section">
        <header className="property__head">
          <div>
            <h1 className="property__name display">{p.name}</h1>
            <p className="muted">
              {[p.address, p.city].filter(Boolean).join(', ')}
              {p.star_rating ? ` · ${'★'.repeat(p.star_rating)}` : ''}
            </p>
          </div>
          {p.phone && (
            <a className="button button--ghost" href={`tel:${p.phone}`}>
              {p.phone}
            </a>
          )}
        </header>

        {p.highlights.length > 0 && (
          <ul className="highlights">
            {p.highlights.map((h) => (
              <li key={h.name} className="highlight">
                {h.name}
              </li>
            ))}
          </ul>
        )}

        {p.description && <p className="property__text">{p.description}</p>}
      </section>

      <section className="section">
        <h2 className="section__title">Выберите номер</h2>
        <SearchBar value={search} onChange={onSearch} compact />

        {!hasDates && <p className="muted">Укажите даты, чтобы увидеть цены и свободные номера.</p>}

        {hasDates && offers.isLoading && <p className="muted">Проверяем свободные номера…</p>}

        {hasDates && offers.data && offers.data.offers.length === 0 && (
          <div className="empty">
            <p className="empty__title">На эти даты свободных номеров нет</p>
            <p className="muted">Попробуйте другие даты.</p>
          </div>
        )}

        {hasDates && offers.data && offers.data.offers.length > 0 && (
          <ul className="rooms">
            {offers.data.offers.map((offer) => (
              <li key={`${offer.room_type_id}-${offer.rate_plan_id}`}>
                <RoomCard
                  offer={offer}
                  room={byId.get(offer.room_type_id)}
                  nightCount={nightCount}
                  currency={offers.data.currency}
                  onBook={() => book(offer)}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {(p.amenities.length > 0 || p.house_rules || p.directions) && (
        <section className="section">
          <h2 className="section__title">Об объекте</h2>
          <div className="facts">
            <div>
              <h3 className="facts__title">Заезд и выезд</h3>
              <p className="muted">
                Заезд с {p.check_in_time.slice(0, 5)}, выезд до {p.check_out_time.slice(0, 5)}
              </p>
            </div>
            {p.directions && (
              <div>
                <h3 className="facts__title">Как добраться</h3>
                <p className="muted">{p.directions}</p>
              </div>
            )}
            {p.house_rules && (
              <div>
                <h3 className="facts__title">Правила</h3>
                <p className="muted">{p.house_rules}</p>
              </div>
            )}
          </div>

          {p.amenities.length > 0 && (
            <>
              <h3 className="facts__title">Удобства</h3>
              <ul className="tags tags--wrap">
                {p.amenities.map((a) => (
                  <li key={a.name} className="tag">
                    {a.name}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      {lightbox !== null && p.photos[lightbox] && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <img src={p.photos[lightbox].url} alt={p.photos[lightbox].alt ?? p.name} />
          <button className="lightbox__close" type="button" aria-label="Закрыть">
            ×
          </button>
        </div>
      )}
    </>
  )
}

function Gallery({
  photos,
  name,
  onOpen,
}: {
  photos: PropertyDetail['photos']
  name: string
  onOpen(i: number): void
}) {
  if (photos.length === 0) return null
  return (
    <div className={photos.length === 1 ? 'gallery gallery--single' : 'gallery'}>
      {photos.slice(0, 5).map((photo, i) => (
        <button
          key={photo.id}
          type="button"
          className="gallery__item"
          onClick={() => onOpen(i)}
          aria-label={`Фотография ${i + 1}`}
        >
          <img src={photo.url} alt={photo.alt ?? name} loading={i === 0 ? 'eager' : 'lazy'} />
        </button>
      ))}
      {photos.length > 5 && <span className="gallery__more">+{photos.length - 5}</span>}
    </div>
  )
}

function RoomCard({
  offer,
  room,
  nightCount,
  currency,
  onBook,
}: {
  offer: Offer
  room: RoomType | undefined
  nightCount: number
  currency: string
  onBook(): void
}) {
  const perNight = Math.round(offer.total_price / Math.max(nightCount, 1))
  return (
    <article className="room">
      {room?.photos[0] && (
        <img className="room__photo" src={room.photos[0].url} alt={room.photos[0].alt ?? offer.room_type_name} loading="lazy" />
      )}

      <div className="room__body">
        <h3 className="room__name">{offer.room_type_name}</h3>
        <p className="muted room__meta">
          {[
            `до ${guests(offer.max_occupancy)}`,
            room?.size_sqm ? `${room.size_sqm} м²` : null,
            room?.view,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>

        {room && room.beds.length > 0 && (
          <p className="muted">{room.beds.map((b) => bed(b.type, b.count)).join(', ')}</p>
        )}

        {room && room.amenities.length > 0 && (
          <ul className="tags">
            {room.amenities.slice(0, 5).map((a) => (
              <li key={a.name} className="tag tag--small">
                {a.name}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="room__price">
        <strong className="room__total">{money(offer.total_price, currency)}</strong>
        <span className="muted">
          {money(perNight, currency)} × {nights(nightCount)}
        </span>
        {offer.available <= 2 && (
          <span className="room__scarce">
            {offer.available === 1 ? 'остался 1 номер' : `осталось ${offer.available}`}
          </span>
        )}
        <button className="button button--primary" type="button" onClick={onBook}>
          Забронировать
        </button>
      </div>
    </article>
  )
}
